import React from 'react';
import { Button, Tag } from 'antd';
import { CheckOutlined, GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import { M2M_SUPPORTED_LANGUAGES } from './m2mLanguages';
import { DEEP_DIVE_AMOUNT, DEEP_DIVE_PRICE, DEEP_DIVE_VALUE, DEEP_DIVE_LINKS, DEEP_DIVE_EXTRAS } from './deepDiveOfferContent';

interface DeepDiveOfferProps {
  drawer?: boolean;
  statusLabel?: string;
  statusMessage?: string;
  actionLabel?: string;
  actionDisabled?: boolean;
  onAction?: () => void;
}

const DeepDiveOffer: React.FC<DeepDiveOfferProps> = ({
  drawer = false,
  statusLabel,
  statusMessage,
  actionLabel,
  actionDisabled = false,
  onAction,
}) => (
  <section
    className={`marketing-m2m-partnership${drawer ? ' marketing-m2m-partnership--drawer' : ''}`}
    aria-label="Novel Publicity Deep Dive report"
  >
    <div className="marketing-m2m-partnership__header">
      <div className="marketing-m2m-partnership__summary">
        <span className="marketing-m2m-partnership__kicker">ScribeShadow exclusive</span>
        <div className="marketing-m2m-partnership__title">
          <h2>Novel Publicity Deep Dive report</h2>
          {statusLabel ? <Tag>{statusLabel}</Tag> : null}
        </div>
        <p>
          ScribeShadow has partnered with Novel Publicity to offer a complete localized marketing
          report built from your translated manuscript.
        </p>
        <div className="marketing-m2m-partnership__languages">
          <GlobalOutlined aria-hidden="true" />
          <span>
            <strong>Available languages:</strong> {M2M_SUPPORTED_LANGUAGES.join(', ')}
          </span>
        </div>
        {statusMessage ? <p className="marketing-m2m-partnership__status">{statusMessage}</p> : null}
        {actionLabel ? (
          <Button
            type="primary"
            icon={<PlusOutlined aria-hidden="true" />}
            disabled={actionDisabled}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        ) : null}
      </div>
      <div className="marketing-m2m-partnership__price" aria-label={`${DEEP_DIVE_PRICE}, ${DEEP_DIVE_VALUE} value`}>
        <span>ScribeShadow price</span>
        <div>
          <strong>{DEEP_DIVE_AMOUNT}</strong>
          <span>per book, per language</span>
        </div>
        <small>{DEEP_DIVE_VALUE} value. Paid at Novel Publicity checkout.</small>
      </div>
    </div>

    <div className="marketing-m2m-partnership__included">
      <h3>Included in every Deep Dive report</h3>
      <ul>
        <li>
          <CheckOutlined aria-hidden="true" />
          <span>
            <strong>Market entry brief</strong>
            Country-specific strategy, reader habits, and Coach’s Corner cultural notes.
          </span>
        </li>
        <li>
          <CheckOutlined aria-hidden="true" />
          <span>
            <strong>Positioning strategy</strong>
            Genre and market positioning for the target-language edition.
          </span>
        </li>
        <li>
          <CheckOutlined aria-hidden="true" />
          <span>
            <strong>Native sales copy</strong>
            Blurbs and taglines in the target language with English versions.
          </span>
        </li>
        <li>
          <CheckOutlined aria-hidden="true" />
          <span>
            <strong>Localized Amazon intelligence</strong>
            Keywords, categories, SEO subtitle guidance, and metadata.
          </span>
        </li>
        <li>
          <CheckOutlined aria-hidden="true" />
          <span>
            <strong>Ad campaign toolkit</strong>
            Ad kits with foreign-market targeting and five video ad concepts.
          </span>
        </li>
      </ul>
    </div>

    <div className="marketing-m2m-partnership__included">
      <h3>Included with your ScribeShadow Deep Dive</h3>
      <ul>{DEEP_DIVE_EXTRAS.map(extra => <li key={extra.title}>
        <CheckOutlined aria-hidden="true" /><span><strong>{extra.title}</strong>{extra.description}</span>
      </li>)}</ul>
      <p>English meanings and the art brief are paid add-ons for public buyers. The excerpt library is available only with the ScribeShadow edition.</p>
      <p>{DEEP_DIVE_LINKS.map(link => <a className="marketing-m2m-partner-link" key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>)}</p>
    </div>

    <div className="marketing-m2m-partnership__delivery">
      <p>
        <strong>Readable in English, ready for the target market.</strong> Strategy and notes to you stay in English;
        reader-facing copy is written for the target language and market, with English meanings for review.
      </p>
      <p>
        <strong>Delivered two ways.</strong> Novel Publicity emails the report, and it is also saved
        here in ScribeShadow.
      </p>
    </div>
  </section>
);

export default DeepDiveOffer;
